export const PROJECT_SETTINGS_PAGES = [
  'general',
  'environment-settings',
  'repositories',
  'databases',
  'synthetic-databases',
  'users',
  'services',
  'security',
] as const

export type ProjectSettingsPage = (typeof PROJECT_SETTINGS_PAGES)[number]

/** @deprecated Tab ids from the old `/project/:id/settings` hub. */
export const PROJECT_SETTINGS_TABS = ['general', 'repositories', 'services', 'security'] as const
export type ProjectSettingsTab = (typeof PROJECT_SETTINGS_TABS)[number]

export const PROJECT_SETTINGS_PAGE_SEGMENTS: Record<ProjectSettingsPage, string> = {
  general: 'general-settings',
  'environment-settings': 'environment-settings',
  repositories: 'repositories',
  databases: 'databases',
  'synthetic-databases': 'synthetic-databases',
  users: 'users',
  services: 'services',
  security: 'security',
}

export const PROJECT_SETTINGS_PAGE_ROUTE_SEGMENTS = PROJECT_SETTINGS_PAGES.map(
  (page) => PROJECT_SETTINGS_PAGE_SEGMENTS[page],
)

export const PROJECT_SETTINGS_TAB_ROUTE_SEGMENTS = ['repositories', 'services', 'security'] as const

export function projectSettingsPagePath(
  projectId: string | number,
  page: ProjectSettingsPage,
): string {
  return `/project/${projectId}/${PROJECT_SETTINGS_PAGE_SEGMENTS[page]}`
}

/** @deprecated Use projectSettingsPagePath. */
export function projectSettingsTabPath(
  projectId: string | number,
  tab: ProjectSettingsTab,
): string {
  if (tab === 'general') return projectSettingsPagePath(projectId, 'general')
  return projectSettingsPagePath(projectId, tab)
}

export function projectSettingsPathSegment(pathname: string, projectId: string): string | null {
  const prefix = `/project/${projectId}/`
  if (!pathname.startsWith(prefix)) return null
  const rest = pathname.slice(prefix.length).replace(/\/$/, '')
  if (!rest || rest.includes('/')) return null
  if (!PROJECT_SETTINGS_PAGE_ROUTE_SEGMENTS.includes(rest)) return null
  return rest
}

export function parseProjectSettingsPageSegment(
  segment: string | null | undefined,
): ProjectSettingsPage {
  if (!segment) return 'general'
  const entry = Object.entries(PROJECT_SETTINGS_PAGE_SEGMENTS).find(([, path]) => path === segment)
  if (entry) return entry[0] as ProjectSettingsPage
  return 'general'
}

/** @deprecated */
export function parseProjectSettingsTabParam(raw: string | null): ProjectSettingsTab {
  const t = (raw ?? 'general').trim().toLowerCase()
  if (PROJECT_SETTINGS_TABS.includes(t as ProjectSettingsTab)) return t as ProjectSettingsTab
  return 'general'
}

/** @deprecated */
export function parseProjectSettingsPathSegment(segment: string | null | undefined): ProjectSettingsTab {
  if (!segment) return 'general'
  if (segment === PROJECT_SETTINGS_PAGE_SEGMENTS.general) return 'general'
  if (segment === PROJECT_SETTINGS_PAGE_SEGMENTS['environment-settings']) return 'general'
  return parseProjectSettingsTabParam(segment)
}

export function legacyProjectSettingsTabToPage(tab: ProjectSettingsTab): ProjectSettingsPage {
  if (tab === 'general') return 'general'
  return tab
}

export function legacyProjectSettingsSegmentToPage(segment: string | null | undefined): ProjectSettingsPage {
  if (!segment) return 'general'
  if (segment === PROJECT_SETTINGS_PAGE_SEGMENTS.general) return 'general'
  if (segment === PROJECT_SETTINGS_PAGE_SEGMENTS['environment-settings']) return 'environment-settings'
  return legacyProjectSettingsTabToPage(parseProjectSettingsTabParam(segment))
}

/** @deprecated */
export function projectSettingsPathNeedsRewrite(
  projectId: string,
  pathname: string,
  tab: ProjectSettingsTab,
): string | null {
  const want = projectSettingsTabPath(projectId, tab)
  const current = pathname.replace(/\/$/, '')
  const wantPath = want.replace(/\/$/, '')
  return current === wantPath ? null : want
}

export type ProjectNavPage = ProjectSettingsPage | 'domains' | 'performance-analysis' | 'ios'

export type ProjectNavItem = {
  page: ProjectNavPage
  label: string
  path: string
}

export type ProjectNavSectionId = 'settings' | 'code' | 'data' | 'telemetry'

export type ProjectNavSection = {
  id: ProjectNavSectionId
  label: string
  tag?: 'beta'
  items: ProjectNavItem[]
}

export function projectDomainsPath(projectId: string | number): string {
  return `/project/${projectId}/domains`
}

export function projectPerformanceAnalysisPath(projectId: string | number): string {
  return `/project/${projectId}/performance-analysis`
}

export function projectIosPath(projectId: string | number): string {
  return `/project/${projectId}/ios`
}

/** Hashes from the short-lived `/project/:id/settings#section` hub. */
const PROJECT_SETTINGS_HASH_ALIASES: Record<string, ProjectNavPage> = {
  general: 'general',
  'general-settings': 'general',
  'environment-defaults': 'environment-settings',
  'environment-settings': 'environment-settings',
  'env-defaults': 'environment-settings',
  security: 'security',
  domains: 'domains',
  repositories: 'repositories',
  services: 'services',
  databases: 'databases',
  'synthetic-databases': 'synthetic-databases',
  'synthetic-dbs': 'synthetic-databases',
  users: 'users',
  'performance-analysis': 'performance-analysis',
  telemetry: 'performance-analysis',
  ios: 'ios',
  phone: 'ios',
  mobile: 'ios',
}

export function parseProjectSettingsHash(hash: string | null | undefined): ProjectNavPage | null {
  const raw = (hash ?? '').replace(/^#/, '').trim().toLowerCase()
  if (!raw) return null
  return PROJECT_SETTINGS_HASH_ALIASES[raw] ?? null
}

export function projectNavPagePath(projectId: string | number, page: ProjectNavPage, search = ''): string {
  const path =
    page === 'domains'
      ? projectDomainsPath(projectId)
      : page === 'performance-analysis'
        ? projectPerformanceAnalysisPath(projectId)
        : page === 'ios'
          ? projectIosPath(projectId)
          : projectSettingsPagePath(projectId, page)
  const qs = !search ? '' : search.startsWith('?') ? search : `?${search}`
  return `${path}${qs}`
}

export type ProjectNavOptions = {
  telemetryEnabled?: boolean
}

export function getProjectNavItems(projectId: string, opts?: ProjectNavOptions): ProjectNavItem[] {
  return getProjectNavSections(projectId, opts).flatMap((section) => section.items)
}

export function getProjectNavSections(projectId: string, opts?: ProjectNavOptions): ProjectNavSection[] {
  const sections: ProjectNavSection[] = [
    {
      id: 'settings',
      label: 'Settings',
      items: [
        { page: 'general', label: 'General', path: projectSettingsPagePath(projectId, 'general') },
        {
          page: 'environment-settings',
          label: 'Env Defaults',
          path: projectSettingsPagePath(projectId, 'environment-settings'),
        },
        { page: 'security', label: 'Security', path: projectSettingsPagePath(projectId, 'security') },
        { page: 'domains', label: 'Domains', path: projectDomainsPath(projectId) },
      ],
    },
    {
      id: 'code',
      label: 'Code',
      items: [
        {
          page: 'repositories',
          label: 'Repositories',
          path: projectSettingsPagePath(projectId, 'repositories'),
        },
        { page: 'services', label: 'Services', path: projectSettingsPagePath(projectId, 'services') },
      ],
    },
    {
      id: 'data',
      label: 'Data',
      items: [
        {
          page: 'databases',
          label: 'Databases',
          path: projectSettingsPagePath(projectId, 'databases'),
        },
        {
          page: 'synthetic-databases',
          label: 'Synthetic DBs',
          path: projectSettingsPagePath(projectId, 'synthetic-databases'),
        },
        {
          page: 'users',
          label: 'Users',
          path: projectSettingsPagePath(projectId, 'users'),
        },
      ],
    },
  ]
  if (opts?.telemetryEnabled) {
    sections.push({
      id: 'telemetry',
      label: 'Telemetry',
      tag: 'beta',
      items: [
        {
          page: 'performance-analysis',
          label: 'Performance Analysis',
          path: projectPerformanceAnalysisPath(projectId),
        },
      ],
    })
  }
  return sections
}

export function isProjectNavItemActive(pathname: string, path: string): boolean {
  const current = pathname.replace(/\/$/, '')
  const target = path.replace(/\/$/, '')
  if (current === target) return true
  const drillIn = target.endsWith('/performance-analysis') || target.endsWith('/ios')
  return drillIn && current.startsWith(`${target}/`)
}

export const PROJECT_SETTINGS_PAGE_TITLES: Record<ProjectSettingsPage, string> = {
  general: 'General',
  'environment-settings': 'Environment defaults',
  repositories: 'Repositories',
  databases: 'Databases',
  'synthetic-databases': 'Synthetic DBs',
  users: 'Users',
  services: 'Services',
  security: 'Security',
}

/** Legacy `/project/:id/settings` path segments (pre-page split). */
export function legacyProjectSettingsHubSegment(pathname: string, projectId: string): string | null {
  const prefix = `/project/${projectId}/settings/`
  if (!pathname.startsWith(prefix)) return null
  const rest = pathname.slice(prefix.length).replace(/\/$/, '')
  if (!rest || rest.includes('/')) return null
  return rest
}

export function isLegacyProjectSettingsHubPath(pathname: string, projectId: string): boolean {
  const base = `/project/${projectId}/settings`
  const normalized = pathname.replace(/\/$/, '')
  return normalized === base || pathname.startsWith(`${base}/`)
}
